import React, { useState, useEffect } from 'react';
import { Col, Input, Label, Row, Spinner, Alert, Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import UploadAnnexure from './UploadAnnexure';
import { BASE_URL } from '../constants/apiConfig';
import './SubmitDocuments.css';

const SubmitDocuments = () => {
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [applicationStatus, setApplicationStatus] = useState(null);
	const [applicationId, setApplicationId] = useState(null);
	const [annexureAFile, setAnnexureAFile] = useState(null);
	const [annexureBFile, setAnnexureBFile] = useState(null);
	const [uploading, setUploading] = useState(false);
	const [autoReminder, setAutoReminder] = useState(false);
	const [showTemplateModal, setShowTemplateModal] = useState(false);
	const [selectedTemplate, setSelectedTemplate] = useState(null);

	useEffect(() => {
		fetchApplicationStatus();
	}, []);

	const fetchApplicationStatus = async () => {
		try {
			setLoading(true);
			const token = sessionStorage.getItem('token');

			// First get the user's application ID
			const response = await axios.get(`${BASE_URL}/api/application/draft`, {
				headers: { Authorization: `Bearer ${token}` },
			});
			console.log(response.data);
			if (response.data.data.applicationId && response.data.data.status === 'Sent for Annexure') {
				const appId = response.data.data.applicationId;
				setApplicationId(appId);

				// Get the application status
				const statusResponse = await axios.get(`${BASE_URL}/api/application/${appId}/status`, {
					headers: { Authorization: `Bearer ${token}` },
				});

				if (statusResponse.data.success) {
					setApplicationStatus(statusResponse.data.data);
				}
			}
		} catch (error) {
			console.error('Error fetching application status:', error);
			toast.error('Failed to fetch application status');
		} finally {
			setLoading(false);
		}
	};

	const handleAnnexureASelect = (file) => {
		setAnnexureAFile(file);
	};

	const handleAnnexureBSelect = (file) => {
		setAnnexureBFile(file);
	};

	const handleTemplateClick = (templateType) => {
		setSelectedTemplate(templateType);
		setShowTemplateModal(true);
	};

	const handleSubmit = async () => {
		if (!annexureAFile || !annexureBFile) {
			toast.warning('Please upload both Annexure A and Annexure B');
			return;
		}

		if (!applicationId) {
			toast.error('Application ID not found');
			return;
		}

		// Validate file types
		if (annexureAFile.type !== 'application/pdf' || annexureBFile.type !== 'application/pdf') {
			toast.error('Only PDF files are allowed for annexure documents');
			return;
		}

		// Validate file sizes (max 5MB each)
		const maxSize = 5 * 1024 * 1024; // 5MB
		if (annexureAFile.size > maxSize || annexureBFile.size > maxSize) {
			toast.error('Each annexure file must be less than 5MB');
			return;
		}

		try {
			setUploading(true);
			const token = sessionStorage.getItem('token');

			const formData = new FormData();
			formData.append('annexureA', annexureAFile);
			formData.append('annexureB', annexureBFile);

			const response = await axios.post(`${BASE_URL}/api/application/${applicationId}/upload/annexure`, formData, {
				headers: {
					Authorization: `Bearer ${token}`,
					'Content-Type': 'multipart/form-data',
				},
			});

			if (response.data.success) {
				toast.success('Annexure A and Annexure B submitted successfully!');
				// Redirect to application status page after a short delay
				setTimeout(() => {
					navigate('/applicationstatus?status=Annexure Received');
				}, 1500);
			}
		} catch (error) {
			console.error('Error uploading annexures:', error);
			toast.error(error.response?.data?.message || 'Failed to upload annexures');
		} finally {
			setUploading(false);
		}
	};

	if (loading) {
		return (
			<div className="container container_cust text-center py-5">
				<Spinner color="primary" />
				<p className="mt-3">Loading application status...</p>
			</div>
		);
	}

	if (!applicationStatus || applicationStatus.status !== 'Sent for Annexure') {
		return (
			<div className="container container_cust">
				<Row>
					<Col xl="12">
						<Alert color="info">
							{applicationStatus?.status === 'Annexure Received'
								? 'Your annexure has been submitted and is under review.'
								: applicationStatus?.status === 'Accepted'
								? 'Congratulations! Your application has been accepted.'
								: 'Annexure submission is not available for your current application status.'}
						</Alert>
					</Col>
				</Row>
			</div>
		);
	}

	return (
		<>
			<div className="container">
				{/* Page Title */}
				<h1 className="page-main-title">Submit Annexure Documents</h1>
				<div className="title-divider"></div>

				<div className="submit-documents-content">
					<div className="shortlist-banner">
						<h2 className="shortlist-title">You are Shortlisted, Submit Annexure A & B</h2>
						<p className="shortlist-description">
							Congratulations! You have been shortlisted. Please upload Annexure A and Annexure B as per the provided templates. These documents are required to proceed further.
						</p>
					</div>

				<Row className="annexure-upload-row">
					<Col md={6} className="annexure-col">
						<div className="annexure-upload-box">
							<div className="upload-box-inner">
								<UploadAnnexure 
									onFileSelect={handleAnnexureASelect} 
									title="Upload Annexure A"
									fileType="A"
								/>
							</div>
							<button 
								className="template-link-btn"
								onClick={() => handleTemplateClick('A')}
							>
								<i className="bi bi-info-circle"></i> Document Templates
							</button>
						</div>
					</Col>

					<Col md={6} className="annexure-col">
						<div className="annexure-upload-box">
							<div className="upload-box-inner">
								<UploadAnnexure 
									onFileSelect={handleAnnexureBSelect} 
									title="Upload Annexure B"
									fileType="B"
								/>
							</div>
							<button 
								className="template-link-btn"
								onClick={() => handleTemplateClick('B')}
							>
								<i className="bi bi-info-circle"></i> Document Templates
							</button>
						</div>
					</Col>
				</Row>					<Row className="reminder-row">
						<Col xl="12">
							<div className="reminder-checkbox">
								<Input
									type="checkbox"
									id="autoReminder"
									checked={autoReminder}
									onChange={(e) => setAutoReminder(e.target.checked)}
								/>
								<Label htmlFor="autoReminder" className="reminder-label">
									Send me auto-reminders until submission
								</Label>
							</div>
						</Col>
					</Row>

					<Row className="submit-row">
						<Col xl="12">
							<button 
								className="submit-documents-btn" 
								onClick={handleSubmit} 
								disabled={uploading || !annexureAFile || !annexureBFile}
							>
								{uploading ? (
									<>
										<Spinner size="sm" className="me-2" />
										Uploading...
									</>
								) : (
									'SUBMIT DOCUMENTS'
								)}
							</button>
						</Col>
					</Row>
				</div>
			</div>

			{/* Document Template Modal */}
			<Modal isOpen={showTemplateModal} toggle={() => setShowTemplateModal(false)} size="lg">
				<ModalHeader toggle={() => setShowTemplateModal(false)}>
					Annexure {selectedTemplate} - Document Template
				</ModalHeader>
				<ModalBody>
					<div className="template-modal-content">
						<Alert color="info">
							<h5>Template Guidelines for Annexure {selectedTemplate}</h5>
						</Alert>
						
						{selectedTemplate === 'A' ? (
							<div className="template-details">
								<h6>Annexure A - Generic Deliverables Timeline</h6>
								<p className="mb-3">A breakdown of the deliverables that align with the fellows' study objectives and their final publication goals.</p>
								
								<div className="mt-3">
									<strong>Months 1-2 (January - February 2026)</strong>
									<ul>
										<li>Submission of a detailed research plan and detailed book structure, including timelines for articles, book chapters, and other deliverables.</li>
										<li>Submission of 2 pieces of interim collateral (e.g., research paper, book chapter, article, case study, op-ed, research summary, blog post, or digital media intervention).
											<ul style={{ listStyleType: 'circle', marginTop: '5px' }}>
												<li>Collateral 1 - <em>Fellow to provide a description of Collateral</em></li>
												<li>Collateral 2 - <em>Fellow to provide a description of Collateral</em></li>
											</ul>
										</li>
									</ul>
								</div>

								<div className="mt-3">
									<strong>Months 3-4 (March - April 2026)</strong>
									<ul>
										<li>Continue research and building on the chapters and sections of the book. Conduct field visits or interviews as relevant.</li>
										<li>Submission of the first few draft chapters.</li>
										<li>Submission of 2 more interim collateral (e.g., research paper, book chapter, article, case study, op-ed, research summary, blog post, or digital media intervention).
											<ul style={{ listStyleType: 'circle', marginTop: '5px' }}>
												<li>Collateral 1 - <em>Fellow to provide a description of Collateral</em></li>
												<li>Collateral 2 - <em>Fellow to provide a description of Collateral</em></li>
											</ul>
										</li>
									</ul>
								</div>

								<div className="mt-3">
									<strong>Months 5-6 (May - June 2026)</strong>
									<ul>
										<li>Continue submission of draft chapters.</li>
										<li>Incorporate feedback and refine chapters.</li>
										<li>Submission of 2 more interim collateral (e.g., research paper, book chapter, article, case study, op-ed, research summary, blog post, or digital media intervention).
											<ul style={{ listStyleType: 'circle', marginTop: '5px' }}>
												<li>Collateral 1 - <em>Fellow to provide a description of Collateral</em></li>
												<li>Collateral 2 - <em>Fellow to provide a description of Collateral</em></li>
											</ul>
										</li>
									</ul>
								</div>

								<div className="mt-3">
									<strong>Months 7-8 (July - August 2026)</strong>
									<ul>
										<li>Submission of the First Draft of the complete book.</li>
										<li>Make revisions based on feedback and refine chapters.</li>
										<li>Submission of 2 more interim collateral (e.g., research paper, book chapter, article, case study, op-ed, research summary, blog post, or digital media intervention).
											<ul style={{ listStyleType: 'circle', marginTop: '5px' }}>
												<li>Collateral 1 - <em>Fellow to provide a description of Collateral</em></li>
												<li>Collateral 2 - <em>Fellow to provide a description of Collateral</em></li>
											</ul>
										</li>
									</ul>
								</div>

								<div className="mt-3">
									<strong>Months 9-10 (September - October 2026)</strong>
									<ul>
										<li>Submission of the Final Draft of the complete book.</li>
										<li>Submission of 2 more interim collateral (e.g., research paper, book chapter, article, case study, op-ed, research summary, blog post, or digital media intervention).
											<ul style={{ listStyleType: 'circle', marginTop: '5px' }}>
												<li>Collateral 1 - <em>Fellow to provide a description of Collateral</em></li>
												<li>Collateral 2 - <em>Fellow to provide a description of Collateral</em></li>
											</ul>
										</li>
									</ul>
								</div>

								<div className="mt-3">
									<strong>Months 11-12 (November - December 2026)</strong>
									<ul>
										<li>Submission of 2 more interim collateral (e.g., research paper, book chapter, article, case study, op-ed, research summary, blog post, or digital media intervention).
											<ul style={{ listStyleType: 'circle', marginTop: '5px' }}>
												<li>Collateral 1 - <em>Fellow to provide a description of Collateral</em></li>
												<li>Collateral 2 - <em>Fellow to provide a description of Collateral</em></li>
											</ul>
										</li>
										<li>Final presentation of the fellowship outcomes, including the book, collaterals, and overall impact.</li>
										<li>Submission of a final fellowship report, summarizing experience and findings.</li>
									</ul>
								</div>

								<div className="mt-4">
									<strong>Additional Notes:</strong>
									<ul>
										<li><strong>Calendered Events:</strong> Fellows will be encouraged to engage in various activities throughout the year, including <em>speaking engagements, panel discussions, and writing or moderating research papers</em>. They will also be offered opportunities to participate in forums, webinars, interviews, and state visits and amplify their contributions through media platforms such as social media, relevant websites, and press releases.</li>
										<li><strong>Periodic Check-ins:</strong> The Knowledge Partners and the Fellowship Team will interact periodically (biweekly or monthly) to facilitate tracking, challenges, and research and writing adjustments.</li>
									</ul>
								</div>
							</div>
						) : (
							<div className="template-details">
								<h6>Annexure B - Conceptual Framework Document Guidelines</h6>
								<p className="mb-3">The following guidelines should be followed, and the specified headings must be used to structure the framework (2000-3000 words max):</p>
								
								<div className="mt-3">
									<ol>
										<li className="mb-3">
											<strong>Focus Area of the Book (Theme):</strong> <em>Clearly define the central theme of the book.</em>
										</li>
										
										<li className="mb-3">
											<strong>Description of the Book's Central Idea in 2 lines (25-50 words max):</strong> <em>Provide a concise description of the book's structure and key topics, capturing its essence and relevance.</em>
										</li>
										
										<li className="mb-3">
											<strong>Tentative Title (3-5 words) and Subtitle (5-10 words) of the Book:</strong> <em>Suggest a working title that encapsulates the book's central theme and objective.</em>
										</li>
										
										<li className="mb-3">
											<strong>Background and Context:</strong> <em>Give a detailed outline of this book, compassing a brief literature review. Include relevant historical, social, or cultural factors that provide a deeper understanding of the topic.</em>
										</li>
										
										<li className="mb-3">
											<strong>Objectives:</strong> <em>Outline the main objectives of the book. What does the book aim to achieve, and what gaps in knowledge will it address?</em>
										</li>
										
										<li className="mb-3">
											<strong>Proposed Methodology:</strong> <em>Describe the research methods, tools, and approaches that will be used to write the book, including data collection techniques, interviews, or any other resources.</em>
										</li>
										
										<li className="mb-3">
											<strong>Detailed Structure with Key Chapters/Sections with a Brief Description of Each Chapter/Section:</strong> <em>Provide a breakdown of the book into chapters or major sections. Include a brief description of each section to highlight what will be covered.</em>
										</li>
										
										<li className="mb-3">
											<strong>Contribution and Significance:</strong> <em>Describe the impact of the book. How will it influence readers, contribute to existing literature, and spark new discussions around the topic?</em>
										</li>
										
										<li className="mb-3">
											<strong>Roadmap/Timeline of the Book (as per Fellowship Timeline):</strong> <em>Provide a timeline of the book's development, outlining key milestones and deadlines in alignment with the fellowship timeline.</em>
										</li>
										
										<li className="mb-3">
											<strong>References:</strong> <em>List some key publications, sources, or works that will be consulted or referenced during the writing process.</em>
										</li>
									</ol>
								</div>
							</div>
						)}
						
						<Alert color="warning" className="mt-3">
							<strong>Note:</strong> Please ensure your timeline document includes all required deliverables and timelines. 
							The document should be in PDF format and not exceed 5MB in size.
						</Alert>
					</div>
				</ModalBody>
				<ModalFooter>
					<Button color="secondary" onClick={() => setShowTemplateModal(false)}>
						Close
					</Button>
				</ModalFooter>
			</Modal>
		</>
	);
};

export default SubmitDocuments;
